import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiClient } from './api-client';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  thumbsUp?: boolean;
  softDelete?: boolean;
}

export interface SendMessageRequest {
  content: string;
  homeworkAssignId: string;
  aiPersona: string;
}

export interface MessageResponse {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  thumbsUp?: boolean;
  softDelete?: boolean;
}

export interface GetMessagesResponse {
  messages: Message[];
}

// Define a type for the history messages sent to the API
export interface ChatHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Get all messages for a specific chat
export const getMessages = async (homeworkAssignId: string): Promise<Message[]> => {
  try {
    const response: AxiosResponse<Message[]> = await ApiClient.get(`/api/v1/gpt/messages/${homeworkAssignId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching messages:', error);
    return [];
  }
};

// Send a new message
export const sendMessage = async (payload: SendMessageRequest): Promise<MessageResponse> => {
  try {
    const formData = new FormData();
    formData.append('content', payload.content);
    formData.append('homeworkAssignId', payload.homeworkAssignId);
    formData.append('aiPersona', payload.aiPersona);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const response: AxiosResponse<MessageResponse> = await ApiClient.post('/api/v1/gpt/sendPrompt', formData, config);
    return response.data;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

// Initialize a new chat session
export const initializeChat = async (homeworkAssignId: string, aiPersona = 'therapist', aiModel = 'gpt-4o-mini'): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append('homeworkAssignId', homeworkAssignId);
    formData.append('aiPersona', aiPersona);
    formData.append('aiModel', aiModel);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    await ApiClient.post('/api/v1/gpt/createMessage', formData, config);
  } catch (error) {
    console.error('Error initializing chat:', error);
    throw error;
  }
};

// Clear chat history
export const clearChat = async (homeworkAssignId: string, aiPersona = 'therapist', aiModel = 'gpt-4o-mini'): Promise<boolean> => {
  try {
    const formData = new FormData();
    formData.append('homeworkAssignId', homeworkAssignId);
    formData.append('aiPersona', aiPersona);
    formData.append('aiModel', aiModel);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    await ApiClient.post('/api/v1/gpt/softDeleteAll', formData, config);
    return true;
  } catch (error) {
    console.error('Error clearing chat:', error);
    return false;
  }
};

// New endpoint for web chat
export const sendWebChatMessage = async (content: string): Promise<MessageResponse> => {
  try {
    const response: AxiosResponse<MessageResponse> = await ApiClient.post('/api/v1/gpt/web/sendMessage', { content });
    return response.data;
  } catch (error) {
    console.error('Error sending web chat message:', error);
    throw error;
  }
};

// New endpoint for web chat with history
export const sendWebChatMessageWithHistory = async (content: string, history: ChatHistoryMessage[]): Promise<MessageResponse> => {
  try {
    const response: AxiosResponse<MessageResponse> = await ApiClient.post('/api/v1/gpt/web/sendMessage', { 
      content,
      history
    });
    return response.data;
  } catch (error) {
    console.error('Error sending web chat message with history:', error);
    throw error;
  }
}; 